import { OrgUnit, isIntake } from 'stores/OrganisationStructure/types';

export type { OrgUnit };
export { isIntake };

export const findUnit = (units: OrgUnit[], uuid: string): OrgUnit | null => {
  for (const unit of units) {
    if (unit.uuid === uuid) return unit;
    if (unit.children) {
      const found = findUnit(unit.children, uuid);
      if (found) return found;
    }
  }
  return null;
};

/** uuids from the root down to and including the given unit. */
export const unitPathIds = (units: OrgUnit[], uuid: string | null): string[] => {
  const trail: string[] = [];
  const walk = (list: OrgUnit[]): boolean => {
    for (const unit of list) {
      trail.push(unit.uuid);
      if (unit.uuid === uuid) return true;
      if (unit.children && walk(unit.children)) return true;
      trail.pop();
    }
    return false;
  };
  if (uuid) walk(units);
  return trail;
};

/** Readable path, for example "Northgate Facilities Group / Building Services / North". */
export const unitPath = (units: OrgUnit[], uuid: string | null): string => {
  if (!uuid) return '';
  const names: string[] = [];
  const walk = (list: OrgUnit[]): boolean => {
    for (const unit of list) {
      names.push(unit.name);
      if (unit.uuid === uuid) return true;
      if (unit.children && walk(unit.children)) return true;
      names.pop();
    }
    return false;
  };
  walk(units);
  return names.join(' / ');
};

export const parentOf = (units: OrgUnit[], uuid: string): OrgUnit | null => {
  const trail = unitPathIds(units, uuid);
  if (trail.length < 2) return null;
  return findUnit(units, trail[trail.length - 2]);
};

/** Every uuid in the subtree rooted at `uuid`, including the root itself. */
export const subtreeIds = (units: OrgUnit[], uuid: string): string[] => {
  const root = findUnit(units, uuid);
  if (!root) return [];
  const ids: string[] = [];
  const walk = (unit: OrgUnit): void => {
    ids.push(unit.uuid);
    (unit.children ?? []).forEach(walk);
  };
  walk(root);
  return ids;
};

/** Units under this one, not counting Intake, which holds records rather than people or units. */
export const unitsUnder = (units: OrgUnit[], uuid: string): number =>
  subtreeIds(units, uuid).filter((id) => !isIntake(findUnit(units, id))).length - 1;

export const flattenUnits = (units: OrgUnit[]): { unit: OrgUnit; depth: number }[] => {
  const rows: { unit: OrgUnit; depth: number }[] = [];
  const walk = (list: OrgUnit[], depth: number): void => {
    list.forEach((unit) => {
      rows.push({ unit, depth });
      if (unit.children) walk(unit.children, depth + 1);
    });
  };
  walk(units, 0);
  return rows;
};

export const collectExpandable = (units: OrgUnit[], acc: Set<string> = new Set()): Set<string> => {
  units.forEach((unit) => {
    if (unit.children && unit.children.length) {
      acc.add(unit.uuid);
      collectExpandable(unit.children, acc);
    }
  });
  return acc;
};

/** Units directly under a parent, or the top level when the parent is null. */
const childrenOf = (units: OrgUnit[], parentId: string | null): OrgUnit[] =>
  parentId === null ? units : (findUnit(units, parentId)?.children ?? []);

const sameName = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** True when another unit directly under this parent already has the name. */
export const nameTakenUnder = (units: OrgUnit[], parentId: string | null, name: string, exceptId?: string): boolean =>
  childrenOf(units, parentId).some((unit) => unit.uuid !== exceptId && sameName(unit.name, name));

/** The error for a name already used under the same parent, naming that parent. */
export const nameTakenMessage = (units: OrgUnit[], parentId: string | null, name: string): string =>
  parentId === null
    ? `A top-level unit is already called ${name}. Choose another name.`
    : `${findUnit(units, parentId)?.name ?? 'That unit'} already has a unit called ${name}. Choose another name.`;

/** Units a unit cannot move under because one of their units already has its name. */
export const parentsWithNameOf = (units: OrgUnit[], unitId: string): string[] => {
  const unit = findUnit(units, unitId);
  if (!unit) return [];
  return flattenUnits(units)
    .map(({ unit: candidate }) => candidate.uuid)
    .filter((candidateId) => nameTakenUnder(units, candidateId, unit.name, unitId));
};

/**
 * Where a deleted unit's contents may go: any unit outside it, except Intake (which holds
 * only records nothing could place) and units that already have a unit with the name of one
 * of its units. Returns the reason for each unit that cannot take them.
 */
export const deleteDestinationBlocks = (units: OrgUnit[], unitId: string): Map<string, string> => {
  const unit = findUnit(units, unitId);
  const reasons = new Map<string, string>();
  if (!unit) return reasons;
  subtreeIds(units, unitId).forEach((id) =>
    reasons.set(id, id === unitId ? 'This is the unit being deleted' : `This unit is under ${unit.name}`)
  );
  flattenUnits(units).forEach(({ unit: candidate }) => {
    if (reasons.has(candidate.uuid)) return;
    if (isIntake(candidate)) {
      reasons.set(candidate.uuid, 'Intake only holds records waiting to be placed');
      return;
    }
    const clash = (unit.children ?? []).find((child) => nameTakenUnder(units, candidate.uuid, child.name, unitId));
    if (clash) reasons.set(candidate.uuid, `Already has a unit called ${clash.name}`);
  });
  return reasons;
};

/** The unit's name, qualified by its parent when another unit shares the name. */
export const shortUnitName = (units: OrgUnit[], uuid: string): string => {
  const unit = findUnit(units, uuid);
  if (!unit) return '';
  const name = unit.name.trim().toLowerCase();
  const shared = flattenUnits(units).some(
    ({ unit: other }) => other.uuid !== unit.uuid && other.name.trim().toLowerCase() === name
  );
  const parent = shared ? parentOf(units, unit.uuid) : null;
  return parent ? `${unit.name} (${parent.name})` : unit.name;
};
